import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  Mail, 
  Key, 
  Loader2, 
  ArrowRight,
  Lock,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminLogin = ({ onLoginSuccess, onBack }) => {
  const [step, setStep] = useState('check'); // check, setup, login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    checkAdminExists();
  }, []);

  const checkAdminExists = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`);
      setStep(response.data.exists ? 'login' : 'setup');
    } catch (err) {
      console.error('Error checking admin:', err);
      setStep('login'); // Default to login
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (!email || !password || !name) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await axios.post(`${API}/admin/setup`, { email, password, name });
      toast.success('Admin account created! Please login.');
      setStep('login');
      setPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create admin account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      
      // Store token and admin info
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminInfo', JSON.stringify(response.data.admin));
      
      toast.success(`Welcome back, ${response.data.admin.name}!`);
      onLoginSuccess(response.data.admin);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'check') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto" data-testid="admin-login">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-8"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-neon-cyan/20 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-neon-cyan" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-white">
            {step === 'setup' ? 'Create Admin Account' : 'Admin Login'}
          </h2>
          <p className="text-slate-400 mt-2">
            {step === 'setup' 
              ? 'Set up your first admin account to access the CRM'
              : 'Sign in to access the Partner CRM dashboard'
            }
          </p>
        </div>

        {/* Error Message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center space-x-3"
            >
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={step === 'setup' ? handleSetup : handleLogin}>
          {/* Name Field (Setup Only) */}
          {step === 'setup' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Full Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full input-dark pl-11"
                  data-testid="admin-name-input"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Shield className="w-5 h-5 text-slate-500" />
                </div>
              </div>
            </div>
          )}

          {/* Email Field */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@medexova.com"
                className="w-full input-dark pl-11"
                data-testid="admin-email-input"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <Mail className="w-5 h-5 text-slate-500" />
              </div>
            </div>
          </div>

          {/* Password Field */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full input-dark pl-11"
                data-testid="admin-password-input"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <Key className="w-5 h-5 text-slate-500" />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn-primary flex items-center justify-center space-x-2"
            data-testid="admin-login-button"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{step === 'setup' ? 'Creating Account...' : 'Signing In...'}</span>
              </>
            ) : (
              <>
                <Lock className="w-5 h-5" />
                <span>{step === 'setup' ? 'Create Account' : 'Sign In'}</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <button
            onClick={onBack}
            className="text-sm text-slate-400 hover:text-white transition-colors"
          >
            Back to Home
          </button>
        </div>

        {/* Security Note */}
        <div className="mt-6 pt-6 border-t border-white/10 text-center">
          <p className="text-xs text-slate-500 flex items-center justify-center space-x-2">
            <Lock className="w-3 h-3" />
            <span>Secure admin access protected by authentication</span>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
